import type {
  MORE_MAPPING_FORM_DATA,
  MORE_TEMPLATE_DATA,
  MoreFieldMappingItem,
} from 'src/interfaces/Template.interface'

export type MoreMappingSaveEntry = {
  values: MORE_MAPPING_FORM_DATA
  fieldMeta: MoreFieldMappingItem
}

export const hasNonEmptyMappingString = (value: unknown): boolean => {
  if (typeof value === 'number' && Number.isFinite(value)) return true
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed || isEmptyAttributeSelectValue(trimmed)) return false
  return true
}

/**
 * Native <select> chose the top placeholder ("Select ITEM_ATTRIBUTES"), not merely an unused empty field.
 * Empty/null sibling fields must not count ΓÇö Default SELECT stores the real id on `value` only.
 */
export const isAttributeSelectPlaceholder = (value: unknown): boolean => {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed || trimmed === 'Loading...') return true
  return /^select\s+/i.test(trimmed)
}

export const isEmptyAttributeSelectValue = (value: unknown): boolean => {
  if (value == null || value === '') return true
  return isAttributeSelectPlaceholder(value)
}

/** True when value is a persisted platform / field-data id (rejects "", null, NaN, placeholders). */
export const hasMeaningfulId = (value: unknown): boolean => {
  if (isEmptyAttributeSelectValue(value)) return false
  const num = Number(value)
  return Number.isFinite(num)
}

/** Replace native-select placeholder labels with null. Do not wipe a filled Default/X-icon value. */
export const sanitizeClearedAttributeSelectValues = (
  values: MORE_MAPPING_FORM_DATA
): MORE_MAPPING_FORM_DATA => {
  const firstRow = values.template_data?.[0]

  return {
    ...values,
    mapped_field: isAttributeSelectPlaceholder(values.mapped_field)
      || isAttributeSelectPlaceholder(firstRow?.mapped_field)
      ? null
      : values.mapped_field,
    value: isAttributeSelectPlaceholder(values.value)
      || isAttributeSelectPlaceholder(firstRow?.value)
      ? null
      : values.value,
    value_str: isAttributeSelectPlaceholder(values.value_str) ? '' : values.value_str,
    template_data: (values.template_data ?? []).map((row) => ({
      ...row,
      mapped_field: isAttributeSelectPlaceholder(row.mapped_field) ? null : row.mapped_field,
      value: isAttributeSelectPlaceholder(row.value) ? null : row.value,
      value_str: isAttributeSelectPlaceholder(row.value_str) ? '' : row.value_str ?? '',
    })),
  }
}

export const normalizeOptionalId = (value: unknown): number | null => {
  if (!hasMeaningfulId(value)) return null
  return Number(value)
}

export const hasMoreMappingRowValue = (
  row: MORE_TEMPLATE_DATA | null | undefined
): boolean => {
  if (!row) return false

  return (
    hasMeaningfulId(row.mapped_field) ||
    hasMeaningfulId(row.value) ||
    hasNonEmptyMappingString(row.value_str)
  )
}

export const isMultilineMoreMappingField = (
  fieldMeta?: MoreFieldMappingItem | null
): boolean => {
  const inputType = `${fieldMeta?.input_type ?? ''}`
  return inputType === 'MULTILINE_TEXT' || inputType === 'MULTILINETEXT'
}

/** Multi-row fields only ΓÇö excludes MULTILINE_* which also contain the substring "MULTI". */
export const isTrueMultiRowMoreMappingField = (
  fieldMeta?: MoreFieldMappingItem | null
): boolean => {
  if (isMultilineMoreMappingField(fieldMeta)) return false
  return Boolean(fieldMeta?.input_type?.includes('MULTI'))
}

export const hasMoreMappingUserValue = (
  values: MORE_MAPPING_FORM_DATA | null | undefined,
  fieldMeta?: MoreFieldMappingItem | null
): boolean => {
  if (!values) return false

  if (isTrueMultiRowMoreMappingField(fieldMeta)) {
    return (values.template_data ?? []).some(hasMoreMappingRowValue)
  }

  if (isMultilineMoreMappingField(fieldMeta)) {
    return (values.template_data ?? []).some((row) =>
      hasNonEmptyMappingString(row?.value_str)
    )
  }

  if (hasMeaningfulId(values.mapped_field)) return true

  const inputType = `${fieldMeta?.input_type ?? ''}`
  if (
    inputType === 'SELECT' ||
    inputType === 'MULTISELECT' ||
    inputType === 'MULTI_INSTANCE_SELECT'
  ) {
    return hasMeaningfulId(values.value)
  }

  return (
    hasMeaningfulId(values.value) ||
    hasNonEmptyMappingString(values.value_str)
  )
}

export const sanitizeMoreMappingTemplateData = (
  values: MORE_MAPPING_FORM_DATA,
  fieldMeta?: MoreFieldMappingItem | null
): MORE_TEMPLATE_DATA[] => {
  if (isMultilineMoreMappingField(fieldMeta)) {
    return (values.template_data ?? [])
      .filter((row) => hasNonEmptyMappingString(row?.value_str))
      .map((row) => ({
        id: row.id ?? null,
        mapped_field: null,
        value: null,
        value_str: row.value_str ?? '',
      }))
  }

  if (!isTrueMultiRowMoreMappingField(fieldMeta)) {
    return []
  }

  return (values.template_data ?? [])
    .filter(hasMoreMappingRowValue)
    .map((row) => ({
      id: row.id ?? null,
      mapped_field: normalizeOptionalId(row.mapped_field),
      value: normalizeOptionalId(row.value),
      value_str: row.value_str ?? '',
    }))
}

export const MANDATORY_MAPPING_API_CODES = [
  'WHEN_MADE',
  'WHO_MADE',
  'READ_LINE_STATE',
  'RETURN_POLICY',
  'SHIPPING_PROFILE',
] as const

export const isMandatoryMoreMappingField = (
  field: MoreFieldMappingItem,
  mandatoryApiCodes: readonly string[] = MANDATORY_MAPPING_API_CODES
): boolean =>
  mandatoryApiCodes.includes(field.api_code) || Boolean(field.isRequired)

export const getMissingMandatoryMappingField = (
  allValues: MORE_MAPPING_FORM_DATA[],
  fieldDefinitions: MoreFieldMappingItem[],
  mandatoryApiCodes: readonly string[] = MANDATORY_MAPPING_API_CODES
): MoreFieldMappingItem | null => {
  for (const field of fieldDefinitions) {
    if (!isMandatoryMoreMappingField(field, mandatoryApiCodes)) continue

    const values = allValues.find(
      (v) => Number(v.platform_field) === Number(field.id)
    )
    if (!hasMoreMappingUserValue(values, field)) {
      return field
    }
  }

  return null
}

export const buildMoreMappingSaveEntries = (
  allValues: MORE_MAPPING_FORM_DATA[],
  fieldDefinitions: MoreFieldMappingItem[]
): MoreMappingSaveEntry[] => {
  const entries: MoreMappingSaveEntry[] = []

  for (const fieldMeta of fieldDefinitions) {
    const values = allValues.find(
      (v) => Number(v.platform_field) === Number(fieldMeta.id)
    )
    if (!values || !hasMoreMappingUserValue(values, fieldMeta)) continue
    entries.push({ values, fieldMeta })
  }

  return entries
}

export const buildMoreMappingPayload = (
  entry: MoreMappingSaveEntry,
  ruleName: string,
  index: number,
  platformId?: number | null
) => {
  const { values, fieldMeta } = entry
  const templateData = sanitizeMoreMappingTemplateData(values, fieldMeta)

  let mappedField = normalizeOptionalId(values.mapped_field)
  let value = normalizeOptionalId(values.value)

  if (isTrueMultiRowMoreMappingField(fieldMeta)) {
    if (templateData.length === 0) {
      mappedField = null
      value = null
    } else {
      const firstRow = templateData[0]
      if (firstRow?.mapped_field != null) {
        mappedField = normalizeOptionalId(firstRow.mapped_field)
      }
      if (firstRow?.value != null) {
        value = normalizeOptionalId(firstRow.value)
      }
    }
  }

  return {
    platform_id: platformId ?? values.platform_id,
    platform_field: normalizeOptionalId(values.platform_field),
    name: index === 0 ? ruleName : `${ruleName}-${index}`,
    mapped_field: mappedField,
    value: value,
    value_str: hasNonEmptyMappingString(values.value_str)
      ? `${values.value_str}`.trim()
      : '',
    isDefault: values.isDefault ?? false,
    template_data: templateData,
  }
}

export type OtherTemplateLink = {
  template_id: number
  fieldId: number | null
}

type MoreMappingRef = {
  fieldId: number | null
  templateId: number
}

export const buildOtherTemplateLinks = (
  saveEntries: MoreMappingSaveEntry[],
  apiResponsesByIndex: Array<{ id?: number } | null | undefined>
): OtherTemplateLink[] => {
  const links: OtherTemplateLink[] = []

  saveEntries.forEach((entry, index) => {
    const responseId = apiResponsesByIndex[index]?.id
    if (responseId == null) return

    links.push({
      template_id: responseId,
      fieldId: normalizeOptionalId(entry.values.platform_field),
    })
  })

  return links
}

/** Field ids whose form has no user-entered mapping value (cleared or never filled). */
export const getMoreMappingFieldIdsWithoutUserValue = (
  allValues: MORE_MAPPING_FORM_DATA[],
  fieldDefinitions: MoreFieldMappingItem[]
): number[] => {
  const clearedFieldIds: number[] = []

  for (const field of fieldDefinitions) {
    const values = allValues.find(
      (v) => Number(v.platform_field) === Number(field.id)
    )
    if (!hasMoreMappingUserValue(values, field)) {
      clearedFieldIds.push(Number(field.id))
    }
  }

  return clearedFieldIds
}

/** Existing templates whose form row is now empty ΓÇö used on edit save. */
export const getMoreMappingFieldIdsToClearOnEdit = (
  allValues: MORE_MAPPING_FORM_DATA[],
  fieldDefinitions: MoreFieldMappingItem[],
  moreMappingIds: MoreMappingRef[]
): number[] => {
  const emptyFieldIds = new Set(
    getMoreMappingFieldIdsWithoutUserValue(allValues, fieldDefinitions)
  )
  if (!emptyFieldIds.size) return []

  const templateFieldIds = new Set<number>()
  moreMappingIds.forEach((ref) => {
    if (ref.fieldId != null) templateFieldIds.add(Number(ref.fieldId))
  })
  fieldDefinitions.forEach((field) => {
    if (field.moreMappingData?.id) templateFieldIds.add(Number(field.id))
  })

  return [...templateFieldIds].filter((id) => emptyFieldIds.has(id))
}

/** Drop recipe links for fields the user cleared so stale templates are not re-submitted. */
export const omitMoreMappingRefsForClearedFields = (
  refs: MoreMappingRef[],
  clearedFieldIds: readonly number[]
): MoreMappingRef[] => {
  if (!clearedFieldIds.length) return refs

  const cleared = new Set(clearedFieldIds.map((id) => Number(id)))

  return refs.filter(
    (ref) => ref.fieldId == null || !cleared.has(Number(ref.fieldId))
  )
}

export const buildClearedMoreMappingPayload = (
  fieldMeta: MoreFieldMappingItem,
  ruleName: string,
  platformId?: number | null
) => ({
  platform_id: platformId ?? null,
  platform_field: normalizeOptionalId(fieldMeta.id),
  name: fieldMeta.moreMappingData?.name || ruleName,
  mapped_field: null,
  value: null,
  value_str: '',
  isDefault: false,
  // Multi-row fields keep their value on template_data rows; an empty list leaves them untouched,
  // so echo the saved row ids back with null values to actually clear them.
  template_data: (fieldMeta.moreMappingData?.template_data ?? [])
    .filter((row: {id?: number | null}) => row?.id != null)
    .map((row: {id?: number | null}) => ({
      id: row.id ?? null,
      mapped_field: null,
      value: null,
      value_str: '',
    })) as MORE_TEMPLATE_DATA[],
})

export type MoreMappingClearJob = {
  existingTemplateId: number
  fieldId: number
  payload: ReturnType<typeof buildClearedMoreMappingPayload>
}

/** PUT empty mapping payloads for optional fields the user cleared on edit. */
export const buildMoreMappingClearJobs = (
  clearedFieldIds: readonly number[],
  moreMappingIds: MoreMappingRef[],
  fieldDefinitions: MoreFieldMappingItem[],
  ruleName: string,
  platformId?: number | null
): MoreMappingClearJob[] => {
  if (!clearedFieldIds.length) return []

  const cleared = new Set(clearedFieldIds.map((id) => Number(id)))
  const jobs: MoreMappingClearJob[] = []
  const seenTemplateIds = new Set<number>()

  const pushJob = (fieldMeta: MoreFieldMappingItem, templateId: number) => {
    if (!templateId || seenTemplateIds.has(templateId)) return
    seenTemplateIds.add(templateId)
    jobs.push({
      existingTemplateId: templateId,
      fieldId: Number(fieldMeta.id),
      payload: buildClearedMoreMappingPayload(fieldMeta, ruleName, platformId),
    })
  }

  moreMappingIds.forEach((ref) => {
    if (!ref.templateId || ref.fieldId == null || !cleared.has(Number(ref.fieldId))) {
      return
    }
    const fieldMeta = fieldDefinitions.find(
      (f) => Number(f.id) === Number(ref.fieldId)
    )
    if (fieldMeta) pushJob(fieldMeta, Number(ref.templateId))
  })

  fieldDefinitions.forEach((fieldMeta) => {
    if (!cleared.has(Number(fieldMeta.id))) return
    const templateId = Number(fieldMeta.moreMappingData?.id)
    if (templateId) pushJob(fieldMeta, templateId)
  })

  return jobs
}
